import { CircularProgress, Grid } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalDialog, RoutalLoader, RoutalPalette, SMPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { NewVehiclePill } from '../../../components/NewVehiclePill';
import { currencyToSymbol, totalPriceWithDiscount } from '../../../constants/Pricing';
import { Project } from '../../../domain/project/Project';
import { Vehicle, weekDays } from '../../../domain/vehicle/Vehicle';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../../hooks/organization/useRetrieveOrganizationResources';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveSubscriptionPricing } from '../../../hooks/subscription/useRetrieveSubscriptionPricing';
import { useCreateVehicles } from '../../../hooks/vehicle/useCreateVehicles';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { isVehicleValid } from '../../settings/projects/new_project_dialog/NewProjectVehicles';

const createVehicle = (vehicles: Vehicle[], project: Project, organizationId: string) =>
  Vehicle.create(
    {
      organization_id: organizationId,
      project_id: project!.id,
      label: ``,
      default_start_location: project.location,
      default_end_location: project.location,
      default_working_days: weekDays,
      default_time_window: Vehicle.getDefaultTimeWindow(),
      color: HexColor.create(Vehicle.getDefaultColor(vehicles)),
      ...(project!.view?.constraints.volume && { default_max_volume: 0 }),
      ...(project!.view?.constraints.weight && { default_max_weight: 0 }),
      enabled: true,
    },
    `id-${Math.random()}`
  );

interface VehiclesFromScratchDialogProps {
  open: boolean;
  handleCloseDialog: (...args: any[]) => any;
}

export const VehiclesFromScratchDialog = ({ open, handleCloseDialog }: VehiclesFromScratchDialogProps) => {
  const intl = useIntl();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [event, setEvent] = useState<any>(null);
  const { data: pricing, isLoading: gettingPricing } = useRetrieveSubscriptionPricing({
    billingPeriod: organization?.subscription.billing_period,
  });
  const { isLoading: creatingVehicles, success: createVehiclesSuccess, run: createVehicles } = useCreateVehicles();

  useEffect(() => {
    if (open && activeProject && organization) {
      setVehicles([createVehicle([], activeProject, organization.id)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (createVehiclesSuccess) {
      setVehicles([createVehicle([], activeProject!, organization!.id)]);

      eventsPanel.addVehicle(`Nuevo`);
      handleCloseDialog(event);

      setEvent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createVehiclesSuccess]);

  const handleCreateVehicles = (e: any) => {
    setEvent(e);
    createVehicles({ vehicles });
  };

  if (!activeProject || !organization || !organizationResources || !projectResources) {
    return null;
  }

  const isConfirmButtonDisabled = () => {
    return (
      creatingVehicles ||
      !vehicles.every(isVehicleValid) ||
      organizationResources.exceedsVehicleHardLimit(vehicles.length)
    );
  };

  const getTotalPrice = () => {
    if (!pricing) return 0;

    const paidVehicles = Math.max(vehicles.length - pricing.missing_extra.vehicles, 0);

    const vehiclesPrice = pricing.vehicles[organization.subscription.plan] * paidVehicles;

    const priceWithTraffic = projectResources?.traffic ? vehiclesPrice + pricing.traffic * paidVehicles : vehiclesPrice;

    return totalPriceWithDiscount({
      price: priceWithTraffic,
      amountOff: pricing.discount?.amount_off,
      percentOff: pricing.discount?.percent_off,
    });
  };

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={handleCloseDialog}
      dialogProps={{
        maxWidth: `lg`,
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
      }}
      contentStyles={{
        padding: 0,
      }}
      titleContent={<Text variant="h6" intlId="vehicles.dialogs.vehicles_from_scratch.title" />}
      dialogContentWarning={<div />}
      showFooter={false}
    >
      <Grid container style={{ height: `600px` }}>
        {gettingPricing ? (
          <Grid
            item
            container
            direction="column"
            style={{ height: `100%`, width: `100%` }}
            alignItems="center"
            justifyContent="center"
          >
            <RoutalLoader />
          </Grid>
        ) : (
          <>
            {pricing ? (
              <>
                <Grid item xs={8} container>
                  <Grid
                    container
                    direction="column"
                    wrap="nowrap"
                    style={{ padding: `20px`, paddingLeft: `40px`, paddingRight: 0 }}
                  >
                    <Grid container style={{ height: `100%`, maxHeight: `calc(100vh - 240px)`, overflowY: `auto` }}>
                      <Grid item container direction="column" wrap="nowrap" alignItems="center">
                        {vehicles.map((vehicle, index) => (
                          <Grid item key={vehicle.id} style={{ width: `100%`, maxWidth: `650px` }}>
                            <NewVehiclePill
                              project={activeProject!}
                              organization={organization!}
                              vehicle={vehicle}
                              constraints={{
                                volume: activeProject!.view.constraints.volume,
                                weight: activeProject!.view.constraints.weight,
                              }}
                              onUpdate={(updatedVehicle: Vehicle) => {
                                const updatedVehicles = vehicles.map((v) => {
                                  if (v.id === updatedVehicle.id) {
                                    return updatedVehicle;
                                  }
                                  return v;
                                });
                                setVehicles(updatedVehicles);
                              }}
                              canRemove={vehicles.length > 1}
                              onRemove={(vehicleToRemove: Vehicle) => {
                                const updatedVehicles = vehicles.filter((v) => v.id !== vehicleToRemove.id);
                                setVehicles(updatedVehicles);
                              }}
                            />

                            {index < vehicles.length - 1 && <VSpacer medium />}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    <VSpacer large />

                    <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="center">
                      <Grid item>
                        <Button
                          variant="flat"
                          intlId="projects.create.create_vehicles.add"
                          onClick={() => {
                            setVehicles([...vehicles, createVehicle(vehicles, activeProject, organization.id)]);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={4}
                  container
                  style={{
                    backgroundColor: SMPalette.grey9,
                    padding: `20px 40px`,
                  }}
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  {!activeProject.managed && organizationResources.subscription.status !== `trialing` && (
                    <>
                      <Grid item>
                        {organizationResources.subscription.has_contract ? (
                          organizationResources.exceedsVehicleHardLimit(vehicles.length) ? (
                            <Text
                              variant="body-medium"
                              intlId={`vehicles.dialogs.vehicles_from_scratch.contract_exceeded`}
                              style={{
                                textAlign: `center`,
                                color: RoutalPalette.error.medium,
                              }}
                            />
                          ) : (
                            <Text
                              intlId={`vehicles.dialogs.vehicles_from_scratch.contract_info`}
                              intlValues={{
                                total: vehicles.length,
                                restantes: Math.max(
                                  organizationResources.subscription.vehicles -
                                    organizationResources.getVehicleCount() -
                                    vehicles.length,
                                  0
                                ),
                              }}
                              style={{
                                textAlign: `center`,
                              }}
                            />
                          )
                        ) : (
                          <Text
                            intlId={`vehicles.dialogs.vehicles_from_scratch.summary_info`}
                            intlValues={{
                              total: vehicles.length,
                              price: `${getTotalPrice()}${currencyToSymbol(pricing.currency)} ${
                                organization.subscription.billing_period
                                  ? intl.formatMessage({ id: `subscription.update.per_month` }).toLocaleLowerCase()
                                  : intl.formatMessage({ id: `subscription.update.per_year` }).toLocaleLowerCase()
                              }`,
                            }}
                            style={{
                              textAlign: `center`,
                            }}
                          />
                        )}
                      </Grid>

                      <VSpacer medium />
                    </>
                  )}

                  <Grid item container direction="row" alignItems="center" justifyContent="center">
                    <Grid item>
                      <Button
                        intlId="cancel"
                        variant="flat"
                        styleType="secondary"
                        style={{
                          minWidth: `100px`,
                        }}
                        onClick={handleCloseDialog}
                      />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      <Button
                        disabled={isConfirmButtonDisabled()}
                        style={{
                          minWidth: `100px`,
                          height: `16px`,
                        }}
                        onClick={handleCreateVehicles}
                      >
                        {creatingVehicles ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Text variant="button" intlId="create" style={{ color: `white` }} />
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </>
        )}
      </Grid>
    </RoutalDialog>
  );
};
